<div *ngIf="isOpen && exportType" class="export-modal-backdrop" (click)="requestBackdropClose()">
  <section class="export-modal-panel" (click)="$event.stopPropagation()">
    <header class="export-modal-header">
      <div>
        <h2 class="export-modal-title">
          {{ exportModalTitle }}
        </h2>
      </div>
      <button type="button" class="export-modal-close" (click)="requestClose()" aria-label="Close export modal">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6 6 18"/>
          <path d="m6 6 12 12"/>
        </svg>
      </button>
    </header>

    <div *ngIf="exportModalState === 'config'" class="export-modal-body">
      <section class="export-modal-section">
        <div class="export-section-header">
          <div class="export-config-section-heading">
            <h3 class="export-section-title export-config-section-title">Choose what to include</h3>
            <p class="vault-page-copy export-config-section-copy">
              Select exactly which parts of your medical record to include in this export.
            </p>
          </div>
          <div class="export-section-actions">
            <span class="vault-status-pill is-active">{{ selectedResourceCount }} selected</span>
            <button
              type="button"
              class="vault-secondary-button export-select-all-button"
              [disabled]="areAllResourcesSelected"
              [class.opacity-50]="areAllResourcesSelected"
              (click)="selectAllResources()">
              Select all
            </button>
          </div>
        </div>

        <div class="export-resource-checklist">
          <label *ngFor="let option of exportResourceOptions; trackBy: trackByResourceType" class="export-resource-card">
            <span class="export-resource-toggle">
              <input
                type="checkbox"
                [checked]="option.selected"
                (change)="toggleExportResource(option.resourceType)">
              <span class="export-resource-checkmark">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M5 13l4 4L19 7"/>
                </svg>
              </span>
            </span>
            <span class="export-resource-copy">
              <span class="export-resource-label">{{ option.label }}</span>
            </span>
          </label>
        </div>
      </section>

      <section *ngIf="requiresTimeboxedAccess" class="export-modal-section">
        <div class="export-config-section-heading">
          <h4 class="export-section-title export-config-section-title">Choose how long access lasts</h4>
          <p class="vault-page-copy export-config-section-copy">
            Choose how long this {{ exportModalTitle.toLowerCase() }} should remain accessible.
          </p>
        </div>

        <div class="export-duration-options">
          <button
            *ngFor="let duration of exportDurationOptions; trackBy: trackByDurationId"
            type="button"
            class="export-duration-button"
            [class.is-selected]="selectedExportDurationId === duration.id"
            (click)="setExportDuration(duration.id)">
            {{ duration.label }}
          </button>
        </div>
      </section>

      <section *ngIf="shouldShowRecaptchaPanel" class="export-modal-section">
        <div class="export-config-section-heading">
          <h4 class="export-section-title export-config-section-title">reCAPTCHA verification</h4>
          <p class="vault-page-copy export-config-section-copy">
            Fasten could not complete the invisible reCAPTCHA v3 security check for this export.
          </p>
        </div>

        <div class="export-recaptcha-card">
          <div class="space-y-1">
            <p class="export-recaptcha-title">reCAPTCHA v3 security check</p>
            <p class="vault-page-copy export-config-section-copy">
              Please review the message below and try confirming again.
            </p>
          </div>
          <span class="vault-status-pill" [ngClass]="recaptchaStatusClass">{{ recaptchaStatusLabel }}</span>
        </div>

        <p *ngIf="recaptchaErrorMessage" class="export-recaptcha-error">{{ recaptchaErrorMessage }}</p>
      </section>
    </div>

    <div *ngIf="exportModalState === 'loading'" class="export-modal-body export-modal-body--centered">
      <div class="export-loading-status">
        <app-spinner [sizePx]="48" [strokeWidthPx]="4"></app-spinner>
        <div class="space-y-2">
          <h3 class="export-section-title">{{ exportLoadingTitle }}</h3>
          <p class="vault-page-copy export-loading-copy">{{ exportLoadingMessage }}</p>
        </div>
      </div>

      <section class="export-modal-section export-loading-section">
        <div class="export-loading-steps">
          <div class="export-loading-step" [class.is-active]="exportLoadingPhase === 'retrieving'" [class.is-complete]="exportLoadingPhase === 'qr-generation'">
            <div>
              <p class="export-step-title">Retrieving medical record data</p>
              <p class="vault-page-copy">Polling export status every 5 seconds.</p>
            </div>
            <p class="export-step-meta">
              Last checked:
              <span>{{ lastStatusCheckLabel }}</span>
            </p>
          </div>

          <div *ngIf="requiresTimeboxedAccess" class="export-loading-step" [class.is-active]="exportLoadingPhase === 'qr-generation'">
            <div>
              <p class="export-step-title">QR Code Generation</p>
              <p class="vault-page-copy">Creating a scannable share experience for the selected export.</p>
            </div>
            <p class="export-step-meta">
              <span>{{ exportLoadingPhase === 'qr-generation' ? 'In progress' : 'Waiting for retrieval' }}</span>
            </p>
          </div>
        </div>
      </section>
    </div>

    <div *ngIf="exportModalState === 'complete'" class="export-modal-body export-modal-body--centered">
      <section class="export-modal-section export-result-section">
        <div class="space-y-2 text-center">
          <h3 class="export-section-title">{{ exportResultTitle }}</h3>
          <p class="vault-page-copy export-result-copy">{{ exportResultSummary }}</p>
        </div>

        <div *ngIf="exportType === 'pdf'" class="export-result-download">
          <div class="export-result-summary-grid">
            <div class="export-result-summary-row">
              <dt>Export type</dt>
              <dd>{{ exportModalTitle }}</dd>
            </div>
            <div class="export-result-summary-row">
              <dt>Institution</dt>
              <dd>{{ accountDisplayName }}</dd>
            </div>
            <div class="export-result-summary-row">
              <dt>Selected categories</dt>
              <dd>{{ selectedResourceListLabel }}</dd>
            </div>
          </div>

          <a
            class="vault-primary-button w-full justify-center px-5 py-4 text-base"
            [href]="downloadUrl"
            download="fasten-health-export.pdf">
            Download
          </a>
          <button type="button" class="vault-secondary-button w-full justify-center px-5 py-4 text-base" (click)="requestClose()">
            Done
          </button>
        </div>

        <div *ngIf="this.exportType !== 'pdf'" class="export-result-qr">
          <div class="mock-qr-frame">
            <img
              *ngIf="qrCodeDataUrl"
              class="generated-qr-code"
              [src]="qrCodeDataUrl"
              [alt]="cardTitle">
          </div>

          <div class="export-result-summary-grid">
            <div class="export-result-summary-row">
              <dt>Export type</dt>
              <dd>{{ exportModalTitle }}</dd>
            </div>
            <div class="export-result-summary-row">
              <dt>Institution</dt>
              <dd>{{ accountDisplayName }}</dd>
            </div>
            <div class="export-result-summary-row">
              <dt>Access duration</dt>
              <dd>{{ selectedDurationLabel }}</dd>
            </div>
            <div *ngIf="smartHealthLinkUrl" class="export-result-summary-row">
              <dt>Link type</dt>
              <dd>{{ cardTitle }}</dd>
            </div>
            <div *ngIf="patientName" class="export-result-summary-row">
              <dt>Patient</dt>
              <dd>{{ patientName }}</dd>
            </div>
            <div *ngIf="patientDob" class="export-result-summary-row">
              <dt>Date of birth</dt>
              <dd>{{ patientDob }}</dd>
            </div>
            <div *ngIf="verifiedDate" class="export-result-summary-row">
              <dt>Verified</dt>
              <dd>{{ verifiedDate }}</dd>
            </div>
          </div>

          <p class="export-result-copy">
            {{ exportQrCodeDescription }}
          </p>

          <div class="flex w-full flex-col gap-3 sm:flex-row">
            <a [href]="resultDocumentUrl" target="_blank" rel="noreferrer" class="vault-primary-button w-full justify-center px-5 py-4 text-base">
              {{ exportResultPrimaryActionLabel }}
            </a>
            <button type="button" class="vault-secondary-button w-full justify-center px-5 py-4 text-base" (click)="requestClose()">
              Done
            </button>
          </div>
        </div>
      </section>
    </div>

    <footer *ngIf="exportModalState === 'config'" class="export-modal-footer">
      <p class="vault-page-copy">{{ selectedResourceCount }} resource types selected</p>
      <p *ngIf="exportRequestErrorMessage" class="export-recaptcha-error export-footer-error">{{ exportRequestErrorMessage }}</p>
      <div class="export-footer-actions">
        <button type="button" class="vault-secondary-button px-5 py-3" (click)="requestClose()">
          Cancel
        </button>
        <button
          type="button"
          class="vault-primary-button px-5 py-3"
          [class.opacity-50]="selectedResourceCount === 0 || isRecaptchaVerifying"
          [disabled]="selectedResourceCount === 0 || isRecaptchaVerifying"
          (click)="confirmExportConfiguration()">
          {{ confirmButtonLabel }}
        </button>
      </div>
    </footer>
  </section>
</div>
